import { ExpandLess } from '@mui/icons-material';
import { IconButton, Stack, Typography } from '@mui/material';
import { calculateTotalOddsForNormalBettingSlip } from 'sportsbook-shared-module';
import { StyleObj } from '../../@types';
import { BETSLIP_WIDTH } from '../../constants';
import {
  useBetslipInfoMessageKeys,
  useBetslipIsWaysTicket,
  useBetslipSelections,
  useBetslipUniqueEventCount,
} from '../../stores/betslip/selectors';
import { getLocalizedBettingTypeFromHitCount } from '../../helpers';
import useLocalization from '../../hooks/useLocalization';
import BetslipBetCount from '../atoms/BetslipBetCount';
import BetslipInfoMessage from '../atoms/BetslipInfoMessage';
import OddsDisplay from '../atoms/OddsDisplay';

const makeStyles = (open: boolean): StyleObj => ({
  container: {
    background: 'white',
    borderRadius: {
      xs: 0,
      sm: '4px 4px 0 0',
    },
    position: 'fixed',
    zIndex: 1500,
    width: {
      xs: '100%',
      sm: BETSLIP_WIDTH,
    },
    bottom: {
      xs: 0,
      md: 32,
    },
    left: {
      xs: 0,
      sm: `calc(50% - ${BETSLIP_WIDTH / 2}px)`,
    },
    cursor: 'pointer',
    transition: '0.3s ease-in-out',
    display: open ? 'none' : 'flex',
  },
  button: {
    alignItems: 'center',
    borderRadius: {
      xs: 0,
      sm: '4px 4px 0 0',
    },
    px: 2,
  },
  iconBtn: {
    padding: 0,
  },
  betCount: {
    flex: 1,
  },
  oddsContainer: {
    alignItems: 'center',
  },
  bettingType: {
    fontWeight: 600,
    color: 'neutral.500',
  },
  totalOdds: {
    fontWeight: 500,
    fontSize: 16,
    color: 'primary.main',
  },
});

type OpenBetslipButtonProps = {
  open: boolean;
  setOpen: React.Dispatch<React.SetStateAction<boolean>>;
};

const OpenBetslipButton = ({ open, setOpen }: OpenBetslipButtonProps) => {
  const bets = useBetslipSelections();
  const infoMessageKeys = useBetslipInfoMessageKeys();
  const uniqueEventCount = useBetslipUniqueEventCount();
  const isWaysTicket = useBetslipIsWaysTicket();
  const styles = makeStyles(open);

  const { t } = useLocalization();

  const preparedBets = bets.map((bet) => {
    return {
      ...bet,
      marketTypeId: bet.marketType.id,
      marketTypeCombiningIds: bet.marketType.marketTypeCombiningIds,
      singlesStakeAmount: Number(bet.singlesStakeAmount),
    };
  });

  const totalOdds = calculateTotalOddsForNormalBettingSlip(preparedBets);

  const localizedBettingType = getLocalizedBettingTypeFromHitCount(t, uniqueEventCount);

  return (
    <Stack sx={styles.container}>
      {Array.from(infoMessageKeys).map((messageKey) => (
        <BetslipInfoMessage key={messageKey} message={t(messageKey)} />
      ))}
      <Stack direction='row' spacing={1} onClick={() => setOpen(!open)} sx={styles.button}>
        <BetslipBetCount numberOfBets={bets.length} sx={styles.betCount} />
        {!isWaysTicket && (
          <Stack direction='row' spacing={1} sx={styles.oddsContainer}>
            <Typography variant='body3' sx={styles.bettingType}>
              1x {localizedBettingType}
            </Typography>
            <OddsDisplay value={totalOdds} variant='h6' sx={styles.totalOdds} />
          </Stack>
        )}
        <IconButton sx={styles.iconBtn}>
          <ExpandLess />
        </IconButton>
      </Stack>
    </Stack>
  );
};

export default OpenBetslipButton;
